import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ConsoleShell from '../../components/admin2/ConsoleShell';
import { marketImageUrl, marketTitle } from '../../lib/marketCatalog';
import { marketCategoryLabel, marketLeafLabel } from '../../lib/marketCategories';
import { buildInquiryCsv, buildInquiryMessage, LEVELS, listMissingWeights, markRequested, PAGE, parseMeasuresCsv, REASONS, saveSupplierMeasures, SORTS, sourceLink, STATUSES } from '../../lib/marketAdminWeights';

// Products with no usable weight or size (owner, 2026-10-27). Their shipping
// is priced from an AI estimate or a fixed 1 kg guess; the supplier knows the
// real figures, and HioBuy asks them by hand until their Supplier Inquiry API
// launches. Select products → copy the message and download the spreadsheet
// for HioBuy → mark them sent → upload the filled sheet (or type one product
// in). Answers are stored as measurements (source 'supplier'), which checkout
// prices from at once and no re-import overwrites. The 1688 link is admin-only.
// Filters (owner, 2026-10-27): no real weight at all, sort by our customers'
// demand or 1688 sales, only products customers asked for, only products on
// the store's collections, category, search. The level and status counts are
// counted inside those filters by the server.

const T = {
  ar: {
    title: 'منتجات بدون وزن', sub: 'منتجات ما لها وزن أو مقاس صالح من المورد — شحنها يُحسب بتقدير. اطلب أوزانها من HioBuy (يسألون المورد يدويًا حاليًا)، ثم ارفع الملف اللي يرجّعونه.',
    level: { noreal: 'بلا وزن حقيقي نهائيًا', none: 'بدون أي بيانات', ai: 'تقدير ذكاء اصطناعي فقط', weight: 'وزن بدون مقاس' },
    sort: { demand: 'الأكثر طلبًا عندنا', sold: 'الأكثر مبيعًا في 1688', newest: 'الأحدث' }, sortBy: 'الترتيب',
    demandOnly: 'عليها طلب من عملائنا', showcaseOnly: 'معروضة في واجهة المتجر', allCats: 'كل التصنيفات', search: 'ابحث بالاسم أو رقم 1688', go: 'بحث', clear: 'مسح',
    orders: 'طلبات مدفوعة', sold: 'مبيعات 1688', showcase: 'في واجهة المتجر',
    status: { open: 'لم تُطلب', requested: 'أُرسلت لـ HioBuy', answered: 'وصل الوزن' },
    selectPage: 'تحديد الصفحة', selected: (n) => `${n} محدد`, copy: 'انسخ رسالة HioBuy', csv: 'حمّل ملف Excel', mark: 'علّمها كمُرسلة',
    copied: 'نُسخت الرسالة', copyFail: 'تعذّر النسخ — انسخها من المربع', marked: (n) => `عُلّم ${n} كمُرسل`, upload: 'ارفع ملف HioBuy المعبّأ',
    imported: (p, s) => `حُفظ ${p} منتج (${s} خيار)`, rejected: 'رُفض', skipped: (n) => `${n} صف بلا وزن — تُرك`, badFile: 'الملف ما فيه عمود maabar_id أو unit_weight_kg',
    demand: 'طلبات سعر (30 يوم)', variants: 'خيارات', link: 'رابط 1688', none: 'لا شيء هنا.', prev: 'السابق', next: 'التالي',
    kg: 'الوزن كجم', l: 'الطول سم', w: 'العرض سم', h: 'الارتفاع سم', save: 'احفظ', saved: 'حُفظ', sentOn: 'أُرسل', answeredOn: 'وصل',
    msgTitle: 'نص الرسالة', close: 'إغلاق',
  },
  en: {
    title: 'Products without weight', sub: 'Products with no usable weight or size from the supplier — their shipping is priced from an estimate. Ask HioBuy for the figures (they ask the supplier by hand for now), then upload the sheet they send back.',
    level: { noreal: 'No real weight at all', none: 'No data at all', ai: 'AI estimate only', weight: 'Weight, no size' },
    sort: { demand: 'Most wanted by our customers', sold: 'Best sellers on 1688', newest: 'Newest' }, sortBy: 'Sort',
    demandOnly: 'Wanted by our customers', showcaseOnly: 'On the store front', allCats: 'All categories', search: 'Search by title or 1688 id', go: 'Search', clear: 'Clear',
    orders: 'Paid orders', sold: '1688 sales', showcase: 'On the store front',
    status: { open: 'Not asked', requested: 'Sent to HioBuy', answered: 'Weight received' },
    selectPage: 'Select page', selected: (n) => `${n} selected`, copy: 'Copy HioBuy message', csv: 'Download Excel sheet', mark: 'Mark as sent',
    copied: 'Message copied', copyFail: 'Copy failed — copy it from the box', marked: (n) => `${n} marked as sent`, upload: 'Upload HioBuy\'s filled sheet',
    imported: (p, s) => `Saved ${p} products (${s} variants)`, rejected: 'Refused', skipped: (n) => `${n} rows without a weight — left out`, badFile: 'The sheet has no maabar_id or unit_weight_kg column',
    demand: 'Price quotes (30 days)', variants: 'variants', link: '1688 link', none: 'Nothing here.', prev: 'Previous', next: 'Next',
    kg: 'Weight kg', l: 'Length cm', w: 'Width cm', h: 'Height cm', save: 'Save', saved: 'Saved', sentOn: 'Sent', answeredOn: 'Received',
    msgTitle: 'Message text', close: 'Close',
  },
};
T.zh = T.en;
const day = (v) => (v ? String(v).slice(0, 10) : '');

function download(name, text) {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function ConsoleMarketWeights({ user, profile, lang }) {
  const isAr = lang === 'ar';
  const c = T[lang] || T.ar;
  // Growth OS «المنتجات» links here with ?q=<1688 id>&status=all&level=… to open on one product.
  const [params] = useSearchParams();
  const urlLevel = params.get('level');
  const [level, setLevel] = useState(LEVELS.includes(urlLevel) ? urlLevel : 'noreal');
  const [status, setStatus] = useState(params.get('status') === 'all' ? 'all' : 'open');
  const [offset, setOffset] = useState(0);
  const [sort, setSort] = useState('demand');
  const [demandOnly, setDemandOnly] = useState(false);
  const [showcaseOnly, setShowcaseOnly] = useState(false);
  const [category, setCategory] = useState('');
  const [q, setQ] = useState(() => (params.get('q') || '').slice(0, 100));       // applied search
  const [qDraft, setQDraft] = useState(() => (params.get('q') || '').slice(0, 100)); // the box
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [sel, setSel] = useState({});            // id → product
  const [msg, setMsg] = useState('');            // the message, shown when copying failed
  const [report, setReport] = useState(null);    // the last import
  const [toast, setToast] = useState('');
  const fileRef = useRef(null);
  const flash = (m) => { setToast(m); setTimeout(() => setToast(''), 2600); };

  const load = useCallback(async () => {
    setData(null); setErr('');
    const r = await listMissingWeights({ level, status, offset, sort, demandOnly, showcaseOnly, category, q });
    if (!r.ok) { setErr(r.error); setData({ rows: [], total: 0, counts: {}, categories: [] }); return; }
    setData(r.data);
  }, [level, status, offset, sort, demandOnly, showcaseOnly, category, q]);
  useEffect(() => { load(); }, [load]);

  const rows = data?.rows || [];
  const picked = Object.values(sel);
  const allOnPage = rows.length > 0 && rows.every((p) => sel[p.id]);
  const togglePage = () => setSel((s) => {
    const n = { ...s };
    if (allOnPage) rows.forEach((p) => delete n[p.id]); else rows.forEach((p) => { n[p.id] = p; });
    return n;
  });
  const toggle = (p) => setSel((s) => { const n = { ...s }; if (n[p.id]) delete n[p.id]; else n[p.id] = p; return n; });

  const copyMessage = async () => {
    const text = buildInquiryMessage(picked);
    try { await navigator.clipboard.writeText(text); flash(c.copied); setMsg(''); } catch { setMsg(text); flash(c.copyFail); }
  };
  const mark = async () => {
    const r = await markRequested(picked.map((p) => p.id));
    if (!r.ok) { flash(r.error); return; }
    flash(c.marked(r.data)); setSel({}); load();
  };
  const onFile = async (file) => {
    if (!file) return;
    const parsed = parseMeasuresCsv(await file.text());
    if (parsed.error) { setReport({ error: parsed.error === 'missing_columns' ? c.badFile : parsed.error }); return; }
    if (!parsed.rows.length) { setReport({ skipped: parsed.skipped, saved_products: 0, saved_skus: 0, rejected: [] }); return; }
    const r = await saveSupplierMeasures(parsed.rows);
    setReport(r.ok ? { ...r.data, skipped: parsed.skipped } : { error: r.error });
    load();
  };

  const counts = data?.counts || {};
  const [cats, setCats] = useState([]);           // kept while a reload is in flight
  useEffect(() => { if (data?.categories) setCats(data.categories); }, [data]);
  const filter = (set) => (v) => { set(v); setOffset(0); };
  const fmt = (n) => Number(n).toLocaleString('en-US');
  const total = Number(data?.total || 0);
  return (
    <ConsoleShell user={user} profile={profile} lang={lang} active="market-weights">
      <div className="ac-page" style={{ fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
        <div className="ac-page-head">
          <div>
            <h1 className="ac-h1">{c.title}</h1>
            <p className="ac-sub">{c.sub}</p>
          </div>
        </div>

        <div className="ac-chiprow" style={{ marginBottom: 8 }}>
          {LEVELS.map((k) => (
            <button key={k} type="button" data-wlevel={k} className={`ac-chip${level === k && status !== 'answered' ? ' on' : ''}`} onClick={() => { setLevel(k); setOffset(0); if (status === 'answered') setStatus('open'); }}>
              {c.level[k]}{counts[k] != null ? ` (${Number(counts[k]).toLocaleString('en-US')})` : ''}
            </button>
          ))}
        </div>
        <div className="ac-chiprow" style={{ marginBottom: 8 }}>
          {STATUSES.map((k) => (
            <button key={k} type="button" data-wstatus={k} className={`ac-chip${status === k ? ' on' : ''}`} onClick={() => { setStatus(k); setOffset(0); }}>
              {c.status[k]}{k !== 'open' && counts[k] != null ? ` (${Number(counts[k]).toLocaleString('en-US')})` : ''}
            </button>
          ))}
        </div>

        <div className="ac-card" data-weight-filters="1" style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 12, fontSize: 13 }}>
          <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            {c.sortBy}
            <select value={sort} data-w-sort="1" onChange={(e) => filter(setSort)(e.target.value)} style={{ height: 32 }}>
              {SORTS.map((k) => <option key={k} value={k}>{c.sort[k]}</option>)}
            </select>
          </label>
          <select value={category} data-w-category="1" onChange={(e) => filter(setCategory)(e.target.value)} style={{ height: 32, maxWidth: 220 }}>
            <option value="">{c.allCats}</option>
            {cats.map((x) => <option key={x.code} value={x.code}>{(marketCategoryLabel(x.code, lang) || x.code)} ({fmt(x.n)})</option>)}
          </select>
          <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            <input type="checkbox" data-w-demand-only="1" checked={demandOnly} onChange={(e) => filter(setDemandOnly)(e.target.checked)} />{c.demandOnly}
          </label>
          <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            <input type="checkbox" data-w-showcase-only="1" checked={showcaseOnly} onChange={(e) => filter(setShowcaseOnly)(e.target.checked)} />{c.showcaseOnly}
          </label>
          <form style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flex: '1 1 240px' }} onSubmit={(e) => { e.preventDefault(); filter(setQ)(qDraft.trim()); }}>
            <input type="search" value={qDraft} data-w-search="1" maxLength={100} placeholder={c.search} onChange={(e) => setQDraft(e.target.value)} dir="auto" style={{ flex: 1, minWidth: 0, height: 32 }} />
            <button type="submit" className="ac-btn ac-btn-sm">{c.go}</button>
            {q && <button type="button" className="ac-btn ac-btn-sm" onClick={() => { setQDraft(''); filter(setQ)(''); }}>{c.clear}</button>}
          </form>
        </div>

        <div className="ac-card" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 12 }}>
          <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
            <input type="checkbox" data-select-page="1" checked={allOnPage} onChange={togglePage} />{c.selectPage}
          </label>
          <span data-selected={picked.length} style={{ fontSize: 12.5, color: '#666' }}>{c.selected(picked.length)}</span>
          <button type="button" className="ac-btn ac-btn-sm" data-copy-message="1" disabled={!picked.length} onClick={copyMessage}>{c.copy}</button>
          <button type="button" className="ac-btn ac-btn-sm" data-download-csv="1" disabled={!picked.length} onClick={() => download(`maabar-weights-${new Date().toISOString().slice(0, 10)}.csv`, buildInquiryCsv(picked))}>{c.csv}</button>
          <button type="button" className="ac-btn ac-btn-sm" data-mark-sent="1" disabled={!picked.length} onClick={mark}>{c.mark}</button>
          <span style={{ flex: 1 }} />
          <input ref={fileRef} type="file" accept=".csv,text/csv,text/plain" hidden data-upload-input="1" onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ''; }} />
          <button type="button" className="ac-btn ac-btn-sm" data-upload="1" onClick={() => fileRef.current?.click()}>{c.upload}</button>
        </div>

        {msg && (
          <div className="ac-card" style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}><b>{c.msgTitle}</b><button type="button" className="ac-btn ac-btn-sm" onClick={() => setMsg('')}>{c.close}</button></div>
            <textarea readOnly dir="ltr" value={msg} style={{ width: '100%', minHeight: 180, fontSize: 12 }} onFocus={(e) => e.target.select()} />
          </div>
        )}

        {report && (
          <div className="ac-card" data-import-report="1" style={{ marginBottom: 12, fontSize: 13 }}>
            {report.error ? <div style={{ color: '#B4372C' }}>{report.error}</div> : (
              <>
                <div data-imported={report.saved_products}>{c.imported(report.saved_products, report.saved_skus)}</div>
                {report.skipped > 0 && <div style={{ color: '#666' }}>{c.skipped(report.skipped)}</div>}
                {(report.rejected || []).length > 0 && (
                  <div data-rejected={report.rejected.length} style={{ marginTop: 6 }}>
                    <b>{c.rejected} ({report.rejected.length})</b>
                    <ul style={{ margin: '4px 0 0', paddingInlineStart: 18 }}>
                      {report.rejected.map((x) => <li key={`${x.row}-${x.product_id}`} dir="auto">#{x.row} · <span dir="ltr">{String(x.product_id || '').slice(0, 8)}</span> — {(REASONS[isAr ? 'ar' : 'en'][x.reason]) || x.reason}</li>)}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {err && <div className="ac-card" style={{ color: '#B4372C', marginBottom: 12 }}>{err}</div>}
        {data == null ? <div className="ac-skel" style={{ height: 120 }} /> : rows.length === 0 ? <div className="ac-placeholder">{c.none}</div> : (
          <div data-weight-list={rows.length} style={{ display: 'grid', gap: 8 }}>
            {rows.map((p) => <WeightRow key={p.id} p={p} c={c} lang={lang} checked={!!sel[p.id]} onToggle={() => toggle(p)} flash={flash} onSaved={load} />)}
          </div>
        )}
        {total > PAGE && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center', marginTop: 14 }}>
            <button type="button" className="ac-btn ac-btn-sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>{c.prev}</button>
            <span dir="ltr" style={{ fontSize: 12.5 }}>{offset + 1}–{Math.min(offset + PAGE, total)} / {total.toLocaleString('en-US')}</span>
            <button type="button" className="ac-btn ac-btn-sm" disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)}>{c.next}</button>
          </div>
        )}
      </div>
      {toast && <div className="ac-toast" data-toast="1">{toast}</div>}
    </ConsoleShell>
  );
}

function WeightRow({ p, c, lang, checked, onToggle, flash, onSaved }) {
  const [v, setV] = useState({ kg: '', l: '', w: '', h: '' });
  const link = sourceLink(p);
  const set = (k) => (e) => setV((x) => ({ ...x, [k]: e.target.value }));
  const save = async () => {
    const n = (s) => (String(s).trim() === '' ? null : Number(String(s).replace(',', '.')));
    const r = await saveSupplierMeasures([{ product_id: p.id, unit_kg: n(v.kg), length_cm: n(v.l), width_cm: n(v.w), height_cm: n(v.h) }]);
    if (!r.ok) { flash(r.error); return; }
    const bad = r.data?.rejected?.[0];
    if (bad) { flash(REASONS[lang === 'ar' ? 'ar' : 'en'][bad.reason] || bad.reason); return; }
    flash(c.saved); onSaved();
  };
  const input = (k, ph) => <input value={v[k]} onChange={set(k)} placeholder={ph} inputMode="decimal" dir="ltr" data-w-input={k} style={{ width: 84, height: 30, fontSize: 12.5 }} />;
  return (
    <div className="ac-card" data-weight-product={p.id} style={{ display: 'grid', gridTemplateColumns: '22px 56px 1fr', gap: 10, alignItems: 'start' }}>
      <input type="checkbox" checked={checked} onChange={onToggle} data-select={p.id} style={{ marginTop: 4 }} />
      {p.image_ref ? <img src={marketImageUrl(p.image_ref, true)} alt="" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 8 }} /> : <div style={{ width: 56, height: 56, background: '#eee', borderRadius: 8 }} />}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13.5 }}>{marketTitle(p, lang)}</div>
        <div style={{ fontSize: 12, color: '#666', margin: '2px 0 6px', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <span>{[marketCategoryLabel(p.category, lang), marketLeafLabel(p.subcategory, lang)].filter(Boolean).join(' › ') || '—'}</span>
          <span>{c.demand}: <b data-demand={p.demand}>{p.demand}</b></span>
          {Number(p.orders) > 0 && <span>{c.orders}: <b data-orders={p.orders}>{p.orders}</b></span>}
          {p.sold != null && <span>{c.sold}: <b data-sold={p.sold}>{Number(p.sold).toLocaleString('en-US')}</b></span>}
          {p.showcase && <span data-showcase="1" style={{ color: '#9A7636', fontWeight: 600 }}>{c.showcase}</span>}
          <span>{p.sku_count} {c.variants}</span>
          {link && <a href={link} target="_blank" rel="noopener noreferrer" data-source-link="1" dir="ltr">{c.link}</a>}
          {p.inq_status === 'requested' && <span data-inq="requested">{c.sentOn} {day(p.requested_at)}</span>}
          {p.inq_status === 'answered' && <span data-inq="answered">{c.answeredOn} {day(p.answered_at)}</span>}
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {input('kg', c.kg)}{input('l', c.l)}{input('w', c.w)}{input('h', c.h)}
          <button type="button" className="ac-btn ac-btn-sm" data-w-save={p.id} disabled={String(v.kg).trim() === ''} onClick={save}>{c.save}</button>
        </div>
      </div>
    </div>
  );
}
